/* Daily efficiency heatmap.
 *
 * A diverging ramp across the month's own efficiency range rather than a
 * fixed scale: the best day in the month is fully blue, the worst fully
 * red, and the midpoint sits near the ground colour. Scaling to the month
 * means a good month still shows its weak days instead of rendering as a
 * flat block of blue.
 *
 * The label colour is picked from the cell's relative luminance rather
 * than assumed, because the ramp passes through both light and dark
 * territory and a fixed foreground would fail at one end.
 */

import { useMemo } from 'react'

type Rgb = [number, number, number]

interface Ramp { lo: Rgb; mid: Rgb; hi: Rgb }

const LIGHT: Ramp = { lo: [168, 50, 63], mid: [238, 241, 244], hi: [47, 95, 143] }
const DARK: Ramp = { lo: [255, 93, 108], mid: [28, 39, 67], hi: [79, 140, 255] }

const mix = (a: Rgb, b: Rgb, k: number): Rgb =>
  [0, 1, 2].map((i) => Math.round(a[i] + (b[i] - a[i]) * k)) as Rgb

/** sRGB relative luminance, for choosing a readable label colour. */
function luminance([r, g, b]: Rgb): number {
  const lin = (c: number) => {
    const u = c / 255
    return u <= 0.03928 ? u / 12.92 : Math.pow((u + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

export function Heatmap({ dates, efficiency, selected, dark, onPick }: {
  dates: string[]
  efficiency: number[]
  selected: number
  dark: boolean
  onPick: (i: number) => void
}) {
  const cells = useMemo(() => {
    const ramp = dark ? DARK : LIGHT
    const lo = Math.min(...efficiency)
    const hi = Math.max(...efficiency)
    return dates.map((d, i) => {
      // t is the day's place in the month's range; k is how far it sits
      // from the middle, so mid-range days stay close to the ground and
      // only the extremes take on full colour.
      const t = hi > lo ? (efficiency[i] - lo) / (hi - lo) : 1
      const k = Math.abs(t - 0.5) * 2
      const rgb = mix(ramp.mid, t >= 0.5 ? ramp.hi : ramp.lo, 0.12 + k * 0.88)
      const dt = new Date(d + 'T00:00:00')
      return {
        key: d,
        day: `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}`,
        val: efficiency[i].toFixed(1) + '%',
        bg: `rgb(${rgb.join(',')})`,
        fg: luminance(rgb) > 0.19 ? '#12161f' : '#ffffff',
      }
    })
  }, [dates, efficiency, dark])

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(92px,1fr))',
      gap: 8, marginTop: 14,
    }}>
      {cells.map((c, i) => (
        <button
          key={c.key}
          type="button"
          onClick={() => onPick(i)}
          aria-label={`${c.key}, ${c.val} efficiency`}
          aria-current={i === selected ? 'true' : undefined}
          style={{
            background: c.bg,
            color: c.fg,
            border: i === selected ? '2px solid var(--color-text)' : '1px solid transparent',
            borderRadius: 0,
            padding: '10px 6px',
            cursor: 'pointer',
            font: 'inherit',
            textAlign: 'center',
            minHeight: 56,
          }}
        >
          <div style={{ fontSize: 11, letterSpacing: '0.08em' }}>{c.day}</div>
          <div className="num" style={{
            fontFamily: 'var(--font-heading)', fontWeight: 600,
            fontSize: 20, lineHeight: 1.1, marginTop: 3,
          }}>{c.val}</div>
        </button>
      ))}
    </div>
  )
}
